import { useTranslation } from "react-i18next";
import i18n from "i18next";
const t = (key: string, fallback?: string) => i18n.t(key, fallback || key);
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFilters } from "./Filters";
import "./style.scss";
import { toNumber } from "lodash";
import { useCurriculumStore } from "../../../../stores/stores";
import useKeyword from "../../../../hooks/useKeyword";
import { COURSE_TYPE, CURRICULUM_SCHEDULE_TYPE, DEFAULT_PAGE_NUM, DEFAULT_PAGE_SIZE, FILTER_STAR_ARR, FREE_OPTIONS, PAID_VALUE_FILTER } from "../../../../constants";
import { CurriculumList } from "../../../../types/curriculum";
import { CoursePayload } from "../../../../types/course";
import { exportCurriculumPrograms, getCourseProgramList } from "../../../../service/curriculum/curriculum-service";
import EducationalProgramCard from "../../../../components/educational-program-card/EducationalProgramCard";
import { getArrayFilterChecked, getArrOrUndefined } from "../../../../utils/arrays";
import { convertSomeObjKeyToArray } from "../../../../utils/format";
import Filter from "../../../../components/filter";
import ResponsiveList from "../../../../components/list/ResponsiveList";
import CustomPagination from "../../../../components/list-program/pagination/Pagination";


type Props = {};

const SearchEducationalProgramsUniversity = (props: Props) => {
  const { t } = useTranslation();
  const params = useParams();

  const { industrieCurriculum } = useCurriculumStore();
  const industryId = toNumber(useKeyword("industry"));
  const university = params?.id || "";
  const keywordUrl = useKeyword("keyword") || "";
  const [defaultOpenKeys, setDefaultOpenKeys] = useState<string[]>([]);

  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState<number>(DEFAULT_PAGE_NUM);
  const [keyword, setKeyword] = useState<string>(useKeyword("keyword") || "");
  const [curriculumList, setCurriculumList] = useState<CurriculumList[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [searchValues, setSearchValues] = useState<CoursePayload>({
    sortByName: "asc",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const filterMenus = useFilters({
    hasPaid: searchValues?.isFreeOptions?.includes(PAID_VALUE_FILTER)
      ? true
      : false,
  });

  const grid = { xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 };

  const renderFilterMenuByParamsUrl = () => {
    if (university)
      return filterMenus.filter((item: any) => item?.key !== "university");

    return filterMenus;
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      const response = await getCourseProgramList({
        ...searchValues,
        keyword: keyword.trim(),
        page: pageNumber,
        size: pageSize,
        industries: industryId ? [industryId] : searchValues.industries,
        universities: [university],
      });
      const { data } = response.data;
      setTotalRecords(data.count);
      setCurriculumList(
        data.curriculumsData.map((item: CurriculumList) => ({
          content: <EducationalProgramCard key={item.id} {...item} />,
        }))
      );
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };

  useEffect(() => {
    setSearchValues((prevState) => ({
      ...prevState,
      keyword: keywordUrl,
    }));
    setKeyword(keywordUrl);
  }, [keywordUrl]);

  useEffect(() => {
    if (university) {
      setDefaultOpenKeys([...defaultOpenKeys, "university"]);
      setSearchValues((prevState) => ({
        ...prevState,
        universities: [university],
      }));
    }
  }, [university]);

  useEffect(() => {
    getData();
  }, [pageNumber, pageSize, searchValues, industryId]);

  const handleChangeFilter = (value: any) => {
    // Filter chuyên ngành
    const industriesFilter = getArrayFilterChecked(
      industrieCurriculum,
      convertSomeObjKeyToArray(value, "industry"),
      "id"
    );
    // Xếp hạng
    const stars = getArrayFilterChecked(
      FILTER_STAR_ARR,
      convertSomeObjKeyToArray(value, "rank"),
      "value"
    );
    // Chi phí
    const freeOptions = getArrayFilterChecked(
      FREE_OPTIONS,
      convertSomeObjKeyToArray(value, "price"),
      "value"
    );

    // Filter lịch trình học
    const courseScheduleType = getArrayFilterChecked(
      CURRICULUM_SCHEDULE_TYPE,
      convertSomeObjKeyToArray(value, "schedule"),
      "value"
    );
    // Filter loại khoá học
    const courseType = getArrayFilterChecked(
      COURSE_TYPE,
      convertSomeObjKeyToArray(value, "kindOfCourse"),
      "value"
    );
    const payload = {
      industries: getArrOrUndefined(industriesFilter),
      universities: [university],
      stars: getArrOrUndefined(stars),
      scheduleType: getArrOrUndefined(courseScheduleType),
      type: getArrOrUndefined(courseType),
      isFreeOptions: getArrOrUndefined(freeOptions),
      minCost: freeOptions?.includes(PAID_VALUE_FILTER) ? 0 : undefined,
      maxCost:
        freeOptions?.includes(PAID_VALUE_FILTER) && value?.cost
          ? value?.cost
          : freeOptions?.includes(PAID_VALUE_FILTER) && !value?.cost
            ? 0
            : undefined,
    };

    setSearchValues((prevState: CoursePayload) => ({
      ...prevState,
      ...payload,
    }));
  };

  const handleChangeSort = (value: number) => {
    switch (value) {
      case 1:
        setSearchValues((prevState: CoursePayload) => ({
          ...prevState,
          ...{ sortByName: "asc", sortByCreatedAt: undefined },
        }));
        break;
      case 2:
        setSearchValues((prevState: CoursePayload) => ({
          ...prevState,
          ...{ sortByName: "desc", sortByCreatedAt: undefined },
        }));
        break;
      case 3:
        setSearchValues((prevState: CoursePayload) => ({
          ...prevState,
          ...{ sortByCreatedAt: "desc", sortByName: undefined },
        }));
        break;
      case 4:
        setSearchValues((prevState: CoursePayload) => ({
          ...prevState,
          ...{ sortByCreatedAt: "asc", sortByName: undefined },
        }));
        break;
      default:
        break;
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await exportCurriculumPrograms({
        ...searchValues,
        page: pageNumber,
        size: pageSize,
        industries: industryId ? [industryId] : searchValues.industries,
      });
      const fileData = res.data;

      const blob = new Blob([fileData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(blob);

      downloadLink.download = `${t("Danh sách chương trình học", "Danh sách chương trình học")}.xlsx`;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    }
  };

  return (
    <div className="search-programs">
      <div className="container">
        <div className="search-programs-result">
          {!industryId && (
            <Filter
              items={renderFilterMenuByParamsUrl()}
              defaultOpenKeys={defaultOpenKeys}
              onChange={handleChangeFilter}
              onChangeSort={handleChangeSort}
            />
          )}

          <div className="search-programs-result__content">
            <ResponsiveList
              loading={isLoading}
              grid={grid}
              data={curriculumList}
              showMenu={true}
              totalRecords={totalRecords}
              // onClickReport = () => {},
              onClickExportExcel={handleExportExcel}
              isViewReport={false}
            />

            {totalRecords > 0 && (
              <CustomPagination
                totalPage={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                setPageSize={setPageSize}
                setPageNumber={setPageNumber}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchEducationalProgramsUniversity;
